import fs from "fs/promises";
import path from "path";
import chalk from "chalk";
import inquirer from "inquirer";
import { parseEnvSchema, mergeSchema, EnvVarInput } from "./schema-ast";

interface GenerateSchemaOptions {
  env: string;
  output: string;
  clientPrefix: string;
  guessTypes: boolean;
}

function inferType(value: string): string {
  const trimmed = value.trim();
  if (/^(true|false)$/i.test(trimmed)) return "z.boolean()";
  if (/^https?:\/\//i.test(trimmed)) return "z.string().url()";
  if (!isNaN(Number(trimmed)) && trimmed !== "") return "z.number()";
  return "z.string()";
}

function formatKey(key: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(key) ? key : `"${key}"`;
}

function isClientVar(key: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => key.startsWith(prefix));
}

const TYPE_CHOICES = [
  { name: "string", value: "z.string()" },
  { name: "number", value: "z.number()" },
  { name: "boolean", value: "z.boolean()" },
  { name: "url", value: "z.string().url()" },
  { name: "enum", value: "enum" },
];

export async function generateSchema(
  options: GenerateSchemaOptions & { nonInteractive?: boolean; sync?: boolean }
) {
  const {
    env,
    output,
    clientPrefix,
    guessTypes,
    nonInteractive = false,
    sync = false,
  } = options;
  const prefixes = clientPrefix
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  let envContent: string;
  try {
    envContent = await fs.readFile(env, "utf-8");
  } catch (e) {
    throw new Error(`Could not read .env file: ${env}`);
  }

  const clientVars: Record<string, EnvVarInput> = {};
  const serverVars: Record<string, EnvVarInput> = {};
  const seenVars: Record<string, Set<string>> = {};
  let lastComment: string | undefined = undefined;

  for (const line of envContent.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) {
      lastComment = undefined;
      continue;
    }
    if (trimmed.startsWith("#")) {
      lastComment = trimmed;
      continue;
    }
    const [key, ...valueParts] = trimmed.split("=");
    if (!key) continue;
    const value = valueParts.join("=");
    if (!seenVars[key]) seenVars[key] = new Set();
    seenVars[key].add(value);
    const type = guessTypes ? inferType(value) : "z.string()";
    const entry = { key, type, comment: lastComment };
    if (isClientVar(key, prefixes)) {
      clientVars[key] = entry;
    } else {
      serverVars[key] = entry;
    }
    lastComment = undefined;
  }

  // Interactive type override for every variable
  async function promptType(
    key: string,
    entry: { type: string; value: string },
    values: Set<string>
  ) {
    if (nonInteractive || !process.stdin.isTTY) {
      // Use inferred type, skip prompt
      if (values.size > 1 && values.size <= 5) {
        // Auto-generate enum if multiple values
        const enumArr = [...values]
          .map((v: string) => JSON.stringify(v))
          .join(", ");
        return `z.enum([${enumArr}])`;
      }
      return entry.type;
    }
    let typeChoices = [...TYPE_CHOICES];
    let defaultType = entry.type;
    let enumValues: string[] = [];
    if (values.size > 1 && values.size <= 5) {
      typeChoices = [
        {
          name: `enum [${[...values]
            .map((v) => JSON.stringify(v))
            .join(", ")}]`,
          value: "enum",
        },
        ...TYPE_CHOICES.filter((c) => c.value !== "enum"),
      ];
      defaultType = "enum";
      enumValues = [...values];
    }
    const { chosenType } = await inquirer.prompt([
      {
        type: "list",
        name: "chosenType",
        message: `Variable: ${key}\nValue: ${entry.value}\nInferred type: ${entry.type}\nPick type:`,
        choices: typeChoices,
        default: typeChoices.findIndex((c) => c.value === defaultType),
      },
    ]);
    let finalType = chosenType;
    if (chosenType === "enum") {
      const { enumInput } = await inquirer.prompt([
        {
          type: "input",
          name: "enumInput",
          message: `Edit enum values for ${key} (comma-separated):`,
          default: enumValues.join(","),
        },
      ]);
      const enumArr = enumInput
        .split(",")
        .map((v: string) => v.trim())
        .filter(Boolean);
      finalType = `z.enum([${enumArr
        .map((v: string) => JSON.stringify(v))
        .join(", ")}])`;
    }
    return finalType;
  }

  // Prompt for all variables
  for (const [key, entry] of Object.entries(clientVars)) {
    clientVars[key].type = await promptType(
      key,
      { type: entry.type, value: entry.key },
      seenVars[key]
    );
  }
  for (const [key, entry] of Object.entries(serverVars)) {
    serverVars[key].type = await promptType(
      key,
      { type: entry.type, value: entry.key },
      seenVars[key]
    );
  }

  // --- SYNC MODE ---
  if (sync && (await fs.stat(output).catch(() => false))) {
    // Parse existing schema
    const parsed = parseEnvSchema(output);
    const mergeResult = mergeSchema(parsed, {
      client: clientVars,
      server: serverVars,
    });
    if (mergeResult.updated) {
      await parsed.sourceFile.save();
      console.log(chalk.green(`🔄 Synced schema at ${output}`));
      if (mergeResult.added.length)
        console.log(chalk.blue(`Added: ${mergeResult.added.join(", ")}`));
      if (mergeResult.updatedVars.length)
        console.log(
          chalk.blue(`Updated: ${mergeResult.updatedVars.join(", ")}`)
        );
      if (mergeResult.removed.length)
        console.log(
          chalk.yellow(`Flagged as removed: ${mergeResult.removed.join(", ")}`)
        );
    } else {
      console.log(chalk.green(`✅ Schema at ${output} is up to date.`));
    }
    return;
  }

  // --- DEFAULT: OVERWRITE MODE ---
  // Generate TypeScript schema
  const schemaLines: string[] = [];
  schemaLines.push("// Generated by envfortress generate-schema");
  schemaLines.push(
    "// Edit this file to refine types or add validation as needed.\n"
  );
  schemaLines.push("import { createEnv } from 'envfortress'");
  schemaLines.push("import { z } from 'zod'\n");
  schemaLines.push("export const env = createEnv({");
  schemaLines.push("  client: {");
  for (const [key, { type, comment }] of Object.entries(clientVars)) {
    if (comment) schemaLines.push(`    ${comment}`);
    let line = `    ${formatKey(key)}: ${type}`;
    const value = seenVars[key] ? Array.from(seenVars[key])[0] : undefined;
    if (
      value &&
      value.trim() !== "" &&
      type !== "z.string()" &&
      !type.startsWith("z.enum")
    ) {
      line += `.default(${JSON.stringify(value.trim())})`;
    }
    line += ",";
    schemaLines.push(line);
  }
  schemaLines.push("  },");
  schemaLines.push("  server: {");
  for (const [key, { type, comment }] of Object.entries(serverVars)) {
    if (comment) schemaLines.push(`    ${comment}`);
    let line = `    ${formatKey(key)}: ${type}`;
    const value = seenVars[key] ? Array.from(seenVars[key])[0] : undefined;
    if (
      value &&
      value.trim() !== "" &&
      type !== "z.string()" &&
      !type.startsWith("z.enum")
    ) {
      line += `.default(${JSON.stringify(value.trim())})`;
    }
    line += ",";
    schemaLines.push(line);
  }
  schemaLines.push("  },");
  schemaLines.push("})\n");
  schemaLines.push("export default env\n");

  // Ensure output directory exists
  const outputDir = path.dirname(output);
  await fs.mkdir(outputDir, { recursive: true });
  await fs.writeFile(output, schemaLines.join("\n"), "utf-8");

  // Print summary
  const clientCount = Object.keys(clientVars).length;
  const serverCount = Object.keys(serverVars).length;
  console.log(chalk.green(`✅ Generated schema at ${output}`));
  console.log(
    chalk.blue(
      `Client variables: ${clientCount}, Server variables: ${serverCount}`
    )
  );
}
